/**
 * @fileoverview Collapsible diff panel showing what the override changes vs the baseline manifest.
 * @module system-web/components/migration/manifest-editor/ManifestDiffPanel
 *
 * Responsibilities:
 * - Show a summary of the active override: excluded tables, added/modified/disabled relationships.
 * - Render the version history (last N entries) from the override document.
 * - Provide a collapsible panel that the admin can expand to review the full diff.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - legacyManifestService (ManifestOverrideRow)
 *
 * Architecture:
 * - Presentation only: receives override doc from useManifestOverride and baseline from props.
 * - No API or Dexie calls.
 *
 * Notes:
 * - When no override exists the panel shows "No changes from baseline."
 * - Version history shows up to 5 recent entries in the panel (full history in a future modal).
 *
 * @reusable
 * Usage: Rendered inside ManifestEditorTab below the tables/relationships panels.
 * Rules: stateful (collapse toggle); presentation-only for all data.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, GitCompare, Clock, User } from 'lucide-react';
import type { ManifestOverrideRow } from '../../../services/legacyManifestService';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract ManifestDiffPanelProps
 *
 * Purpose:
 * - Props for the ManifestDiffPanel component.
 *
 * Fields:
 * - override: The active manifest override from Dexie, or null if no override exists.
 * - baselineRelationshipCount: Number of relationships in the baseline manifest.
 * - baselineTableCount: Number of tables in the baseline manifest.
 *
 * Rules:
 * - When override is null, the panel shows "No changes from baseline."
 *
 * Sync:
 * - override is provided by useManifestOverride (Dexie liveQuery).
 */
export interface ManifestDiffPanelProps {
  override: ManifestOverrideRow | null;
  baselineRelationshipCount: number;
  baselineTableCount: number;
}

// ---------------------------------------------------------------------------
// History entry shape (subset of ManifestOverrideHistoryEntry)
// ---------------------------------------------------------------------------

interface HistoryEntry {
  version: number;
  updatedAt: string;
  updatedBy: string;
  changeNote?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ManifestDiffPanel
 *
 * Purpose:
 * - Collapsible summary diff between the baseline and the effective manifest.
 *
 * Props:
 * - See ManifestDiffPanelProps.
 *
 * Behavior:
 * - Collapsed by default; header shows version number and change count chips.
 * - Expanded: shows three sections:
 *   1. Table changes — excluded table count.
 *   2. Relationship changes — added / modified / disabled counts.
 *   3. Version history — last 5 entries with user, date, and optional change note.
 * - When override is null, shows "No active override — effective manifest equals baseline."
 *
 * Data Source:
 * - Props only; no fetching.
 *
 * Constraints:
 * - No Dexie reads or writes; no API calls.
 *
 * Performance:
 * - Lightweight; no lists larger than 20 entries.
 *
 * @reusable
 * Usage: One per active dataset inside ManifestEditorTab.
 * Rules: stateful (open/close toggle only); presentation-only.
 */
export function ManifestDiffPanel({
  override,
  baselineRelationshipCount,
  baselineTableCount,
}: ManifestDiffPanelProps) {
  const [open, setOpen] = useState(false);

  const excludedCount = override?.excludedTables?.length ?? 0;
  const addedCount = override?.addedRelationships?.length ?? 0;
  const modifiedCount = (override?.modifiedRelationships as HistoryEntry[] | undefined)?.length ?? 0;
  const historyEntries: HistoryEntry[] = ((override?.history as HistoryEntry[]) ?? []).slice(-5).reverse();
  const totalChanges = excludedCount + addedCount + modifiedCount;

  return (
    <div className="rounded-lg border" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      {/* Header / toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <GitCompare className="h-4 w-4 text-gray-500" />
          Override Diff
          {override && !override._deleted && (
            <span className="text-xs font-normal text-gray-500 ml-1">v{override.version}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {override && !override._deleted && totalChanges > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
              {totalChanges} change{totalChanges !== 1 ? 's' : ''}
            </span>
          )}
          {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="border-t px-4 py-3 space-y-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>

          {/* No override */}
          {(!override || override._deleted) && (
            <p className="text-sm text-gray-500">
              No active override — effective manifest equals the baseline.
            </p>
          )}

          {override && !override._deleted && (
            <>
              {/* Table changes */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Tables</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-gray-600">Baseline tables</span>
                  <span className="font-mono">{baselineTableCount}</span>
                  <span className="text-gray-600">Excluded by override</span>
                  <span className={`font-mono ${excludedCount > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                    {excludedCount}
                  </span>
                  <span className="text-gray-600">Effective tables</span>
                  <span className="font-mono font-semibold">{baselineTableCount - excludedCount}</span>
                </div>
                {excludedCount > 0 && (
                  <ul className="mt-1.5 ml-3 space-y-0.5">
                    {override.excludedTables.map((t) => (
                      <li key={t} className="text-xs font-mono text-red-600">
                        − {t}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Relationship changes */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Relationships</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-gray-600">Baseline relationships</span>
                  <span className="font-mono">{baselineRelationshipCount}</span>
                  {addedCount > 0 && (
                    <>
                      <span className="text-gray-600">Added (manifest-only)</span>
                      <span className="font-mono text-green-700 font-semibold">+{addedCount}</span>
                    </>
                  )}
                  {modifiedCount > 0 && (
                    <>
                      <span className="text-gray-600">Modified</span>
                      <span className="font-mono text-amber-700 font-semibold">{modifiedCount}</span>
                    </>
                  )}
                </div>
              </section>

              {/* Version history */}
              {historyEntries.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Recent History</h3>
                  <ul className="space-y-1.5">
                    {historyEntries.map((entry) => (
                      <li key={entry.version} className="flex items-start gap-2 text-xs text-gray-600">
                        <Clock className="h-3 w-3 mt-0.5 shrink-0 text-gray-400" />
                        <div>
                          <span className="font-mono font-medium">v{entry.version}</span>
                          <span className="mx-1 text-gray-400">·</span>
                          <span>{new Date(entry.updatedAt).toLocaleString()}</span>
                          {entry.updatedBy && (
                            <>
                              <span className="mx-1 text-gray-400">·</span>
                              <span className="inline-flex items-center gap-0.5">
                                <User className="h-2.5 w-2.5" />
                                {entry.updatedBy}
                              </span>
                            </>
                          )}
                          {entry.changeNote && (
                            <p className="text-gray-500 italic mt-0.5">{entry.changeNote}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
